import { Box, Container, Divider, Grid, Skeleton, Stack, styled, Typography, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import { get } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

//Components
import FormikForm from 'src/components/common/FormikForm';
import BillingInformation from 'src/features/Checkout/BillingInformation';
import CheckOrder from 'src/features/Checkout/CheckOrder';
import PaymentMethod from 'src/features/Checkout/PaymentMethod';

// Utilities
import { CheckoutStep } from 'src/features/Checkout/config';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryBillingDetail, useQueryCustomerId, useQueryPlanDetail } from 'src/services/queries';
import { TypePlanPrice } from 'src/utils/constants/enum';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { renderPriceOrDefault } from 'src/utils/helpers/stringHelpers';
import { ICheckoutForm } from 'src/utils/typings/dto/payment';
import { SectionErrorBoundary } from 'src/components/ErrorBoundary/SectionErrorBoundary';

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius * 2,
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(7),
}));

const Checkout = () => {
  const theme = useTheme();
  const { campaignSlug, planId, type, error_code } = useQueryParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState<CheckoutStep>(error_code ? CheckoutStep.PaymentMethod : CheckoutStep.CheckOrder);
  const { data: dataBilling } = useQueryBillingDetail();
  const { data: dataPlan, isFetching } = useQueryPlanDetail(planId, campaignSlug);

  // const planDetail = MOCK_DATA.find((plan) => plan.id === planId);
  const planDetail = dataPlan?.data;
  const listPrices = {
    ...(planDetail?.price_monthly && {
      [TypePlanPrice.Month]: planDetail.price_monthly,
    }),
    ...(planDetail?.price_quarterly && {
      [TypePlanPrice.Quarter]: planDetail.price_quarterly,
    }),
    ...(planDetail?.price_half_yearly && {
      [TypePlanPrice.HalfYear]: planDetail.price_half_yearly,
    }),
    ...(planDetail?.price_yearly && {
      [TypePlanPrice.Year]: planDetail.price_yearly,
    }),
  };

  const formik = useFormik<ICheckoutForm>({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      plan_type: type,
      first_name: dataBilling?.data?.first_name || '',
      last_name: dataBilling?.data?.last_name || '',
      email: dataBilling?.data?.email || '',
    },
    validationSchema: Yup.object().shape({
      plan_type: Yup.string().max(255),
      first_name: Yup.string().max(255).required('First name is required'),
      last_name: Yup.string().max(255).required('Last name is required'),
      email: Yup.string().max(255).email('Email is invalid').required('Email is required'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(false);
    },
  });

  useEffect(() => {
    if (!planId) {
      navigate(AuthRouteKeys.Pricing);
    }
  }, []);

  const handleChangeStep = (stepSelect: CheckoutStep) => {
    switch (stepSelect) {
      case CheckoutStep.CheckOrder:
        setStep(CheckoutStep.CheckOrder);
        break;
      case CheckoutStep.BillingInformation:
        if (formik.values.plan_type) {
          setStep(CheckoutStep.BillingInformation);
        }
        break;
      case CheckoutStep.PaymentMethod:
        formik.submitForm();
        if (formik.isValid) {
          setStep(CheckoutStep.PaymentMethod);
        }
        break;

      default:
        break;
    }
  };

  if (isFetching) {
    return (
      <StyledContainer maxWidth="xl">
        <Box
          sx={{
            height: '100%',
            background: theme.palette.background.paper1,
          }}
        >
          <Grid container spacing={3} pt={3}>
            <Grid item sm={12} md={12} lg={9} xl={9}>
              <Skeleton variant="rectangular" animation="wave" height={150} />
            </Grid>
            <Grid item sm={12} md={12} lg={3} xl={3}>
              <Skeleton variant="rectangular" animation="wave" height={150} />
            </Grid>
          </Grid>
        </Box>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer maxWidth="xl">
      <Box
        sx={{
          height: '100%',
          background: theme.palette.background.paper1,
        }}
      >
        <Typography variant="h5">{t('checkout.title')}</Typography>
        <Grid container spacing={3} pt={3}>
          <SectionErrorBoundary resetKey="CheckoutStep" sectionName="CheckoutStep">
            <Grid item sm={12} md={12} lg={9} xl={9}>
              <FormikForm formik={formik}>
                <Stack direction="column" spacing={3}>
                  <StyledBox>
                    <CheckOrder
                      open={step === CheckoutStep.CheckOrder}
                      isStepDone={[CheckoutStep.BillingInformation, CheckoutStep.PaymentMethod].includes(step)}
                      handleChangeStep={handleChangeStep}
                      listPrices={listPrices}
                      planDetail={planDetail}
                    />
                  </StyledBox>
                  <StyledBox>
                    <BillingInformation
                      open={step === CheckoutStep.BillingInformation}
                      isStepDone={[CheckoutStep.PaymentMethod].includes(step)}
                      handleChangeStep={handleChangeStep}
                    />
                  </StyledBox>

                  <StyledBox>
                    <PaymentMethod
                      open={step === CheckoutStep.PaymentMethod}
                      handleChangeStep={handleChangeStep}
                      planDetail={planDetail}
                      campaignSlug={campaignSlug}
                    />
                  </StyledBox>
                </Stack>
              </FormikForm>
            </Grid>
          </SectionErrorBoundary>
          <SectionErrorBoundary resetKey="CheckoutSummary" sectionName="CheckoutSummary">
            <Grid item sm={12} md={12} lg={3} xl={3}>
              <StyledBox>
                <Typography variant="h6">{t('checkout.summary_order.title')}</Typography>
                <Stack direction="column" spacing={2} mt={2.5}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t('checkout.summary_order.plan_name')}
                    </Typography>
                    <Typography variant="subtitle2" align="right">
                      {planDetail.plan_name}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t('checkout.summary_order.price')}
                    </Typography>
                    <Typography variant="subtitle1">
                      ${renderPriceOrDefault(get(planDetail, `price_${formik.values.plan_type}`, 0))}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t('checkout.summary_order.type')}
                    </Typography>
                    <Typography variant="subtitle2">{t(`pricing.plan_type.${formik.values.plan_type}`)}</Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t('checkout.summary_order.total')}
                    </Typography>
                    <Typography variant="h6">
                      ${renderPriceOrDefault(get(planDetail, `price_${formik.values.plan_type}`, 0))}
                    </Typography>
                  </Stack>
                </Stack>
              </StyledBox>
            </Grid>
          </SectionErrorBoundary>
        </Grid>
      </Box>
    </StyledContainer>
  );
};

export default Checkout;
